import React from 'react';
import Datetime from 'react-datetime';
import moment from 'moment';
import 'react-datetime/css/react-datetime.css';

/**
 * DateInput
 *
 * Uses react-datetime's custom calendar popup — navigating months never fires
 * onChange, only clicking a specific day does. This eliminates the browser's
 * native auto-select-on-month-navigation behaviour entirely.
 *
 * The picker is position:absolute within the .rdt wrapper so it scrolls
 * naturally with the rest of the panel content.
 *
 * Props:
 *   value       {string}   YYYY-MM-DD string (or '') for the committed date
 *   onChange    {function} called with YYYY-MM-DD string, or null to clear
 *   className   {string}   applied to the underlying <input>
 *   min         {string}   YYYY-MM-DD — dates before this are disabled
 *   placeholder {string}   input placeholder (default 'MM/DD/YYYY')
 *   allowManualInput {boolean} when true, allows typing in the input
 */
function DateInput({ value, onChange, className, min, placeholder, allowManualInput = false }) {
	const [typedValue, setTypedValue] = React.useState(value ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : '');

	React.useEffect(() => {
		if (!allowManualInput) return;
		setTypedValue(value ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : '');
	}, [allowManualInput, value]);

	const formatForTyping = (raw) => {
		const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
		if (digits.length <= 2) return digits;
		if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
		return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
	};

	const parseTyped = (raw) => moment(raw, ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD', 'MMDDYYYY'], true);

	const isValidDate = min
		? (current) => current.isSameOrAfter(moment(min, 'YYYY-MM-DD'), 'day')
		: undefined;

	const minMoment = min ? moment(min, 'YYYY-MM-DD') : null;
	const commitIfValid = (dateMoment) => {
		if (!dateMoment?.isValid()) return;
		if (minMoment && dateMoment.isBefore(minMoment, 'day')) return;
		onChange(dateMoment.format('YYYY-MM-DD'));
	};

	return (
		<Datetime
			value={allowManualInput ? typedValue : (value ? moment(value, 'YYYY-MM-DD') : '')}
			dateFormat="MM/DD/YYYY"
			timeFormat={false}
			closeOnSelect
			onChange={(date) => {
				if (moment.isMoment(date) && date.isValid()) {
					if (allowManualInput) setTypedValue(date.format('MM/DD/YYYY'));
					commitIfValid(date);
				} else if (date === '' || date === null || date === undefined) {
					if (allowManualInput) setTypedValue('');
					onChange(null);
				} else if (allowManualInput && typeof date === 'string') {
					const formatted = formatForTyping(date);
					setTypedValue(formatted);
					const parsed = parseTyped(formatted);
					if (parsed.isValid()) {
						setTypedValue(parsed.format('MM/DD/YYYY'));
						commitIfValid(parsed);
					}
				}
			}}
			inputProps={{
				className,
				placeholder: placeholder || 'MM/DD/YYYY',
				readOnly: !allowManualInput,
				maxLength: allowManualInput ? 10 : undefined,
				onBlur: allowManualInput
					? () => {
						const parsed = parseTyped(typedValue);
						if (parsed.isValid()) {
							setTypedValue(parsed.format('MM/DD/YYYY'));
							commitIfValid(parsed);
						} else {
							setTypedValue(value ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : '');
						}
					}
					: undefined,
			}}
			isValidDate={isValidDate}
		/>
	);
}

export default DateInput;
